import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getNearbyAmbulanceProviders } from "../../http";
import { UseGetStoreData } from "../../hooks/getStoreData/UseGetStoreData";
import { setAmbulanceMessage, setShortlistedProviders } from "../../redux/ticketUpdateSlice/ticketUpdateSlice";
import { AmbulanceProviderCard, AmbulanceProviderData } from "../../components/molecules/ambulanceProviderCard/AmbulanceProviderCard";
import style from "./ambulanceProviders.module.scss";

export const AmbulanceProvidersPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getTicketData, getTicketUpdateData, getAmbulanceMessage } = UseGetStoreData();
  const [providers, setProviders] = useState<AmbulanceProviderData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shortlistedProviders, setShortlistedProvidersLocal] = useState<AmbulanceProviderData[]>([]);
  const reduxShortlisted = useSelector((state: any) => state?.tickUpdate?.shortlistedProviders ?? []);
  const reduxSelectedProviderId = useSelector((state: any) => state?.tickUpdate?.selectedProviderId ?? null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Restore shortlisted from Redux on mount
  useEffect(() => {
    if (reduxShortlisted.length > 0) setShortlistedProvidersLocal(reduxShortlisted);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleShortlist = (provider: AmbulanceProviderData) => {
    setShortlistedProvidersLocal((prev) => {
      const updated = prev.some((p) => p._id === provider._id)
        ? prev.filter((p) => p._id !== provider._id)
        : [...prev, provider];
      dispatch(setShortlistedProviders(updated));
      return updated;
    });
  };

  const coordinates = getTicketData?.patientLocation?.geoLocation?.coordinates;
  const latitude: number | undefined = coordinates?.[0];
  const longitude: number | undefined = coordinates?.[1];

  // Build and store the ambulance message on mount (and whenever ticket data changes)
  // so the WhatsApp button always has the message even if the agent never clicked
  // the "Ambulance Message" button on the booking screen.
  useEffect(() => {
    if (!getTicketData?._id) return;
    // Only auto-build if no message has been stored yet (i.e. booking screen wasn't visited)
    if (getAmbulanceMessage) return;

    const pickupCoords = getTicketData?.patientLocation?.geoLocation?.coordinates;
    const dropCoords = getTicketData?.hospitalInfo?.geoLocation?.coordinates;
    const pickupMapLink = pickupCoords ? `https://www.google.com/maps?q=${pickupCoords[0]},${pickupCoords[1]}` : "N/A";
    const dropMapLink = dropCoords ? `https://www.google.com/maps?q=${dropCoords[0]},${dropCoords[1]}` : "N/A";

    const beneficiary = getTicketData?.patient?.fullName || getTicketData?.ticketRaisedBy?.fullName || "N/A";
    const beneficiaryUId = getTicketData?.patient?.id || getTicketData?.ticketRaisedBy?.benId || "N/A";
    const contactperson = getTicketData?.poc?.fullName || "N/A";
    const contactnumber = getTicketData?.poc?.mobile || "N/A";
    const patientCondition = [
      ...(getTicketData?.currentMedicalStatusResponse?.currentCondition || []),
      ...(getTicketData?.currentMedicalStatusResponse?.otherDescription
        ? [getTicketData.currentMedicalStatusResponse.otherDescription]
        : []),
    ].join(", ") || "N/A";
    const ambulancetype = getTicketUpdateData?.selectedAmbulanceType || "N/A";
    const hopitalName = getTicketData?.hospitalInfo?.hospitalName || "";
    const dropaddress = `${hopitalName}${getTicketData?.hospitalInfo?.address || ""}` || "N/A";

    const assistanceReq = getTicketUpdateData?.typeOfAssistanceRequired;
    const assistanceItems: string[] = [];
    if (assistanceReq?.wheelChair) assistanceItems.push("wheelChair");
    if (assistanceReq?.stretcher) assistanceItems.push("stretcher");
    if (assistanceReq?.paramedic) assistanceItems.push("paramedic");
    if (assistanceReq?.additionalHelp) assistanceItems.push("additionalHelp");
    if (assistanceReq?.DoctorOnBoard) assistanceItems.push("DoctorOnBoard");
    const requirement = assistanceItems.length > 0 ? assistanceItems.join(", ") : "N/A";
    const noofhelper = assistanceReq?.additionalHelp
      ? (assistanceReq?.numberOfHelpers || "N/A")
      : "N/A";

    const fields = [
      { label: "Booking ID", value: beneficiaryUId },
      { label: "Contact Person", value: `${contactperson} | ${contactnumber}` },
      { label: "Patient Condition", value: patientCondition },
      { label: "Ambulance Type", value: ambulancetype },
      { label: "Requirements", value: requirement },
      { label: "No. of Helpers", value: String(noofhelper) },
      { label: "Pickup Address", value: getTicketData?.patientLocation?.address || "N/A" },
      { label: "Pickup Map", value: pickupMapLink },
      { label: "Drop Address", value: dropaddress },
      { label: "Drop Map", value: dropMapLink },
      { label: "Pickup Time", value: "Immediately" },
    ];
    const subHeader = `An ambulance booking has been created for ${beneficiary} based on a service request.`;
    const messageText = `*Ambulance Request from Pococare*\n${subHeader}\n\n` + fields.map(f => `${f.label}: ${f.value}`).join("\n\n");
    dispatch(setAmbulanceMessage(messageText));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTicketData?._id]);

  useEffect(() => {
    if (!latitude || !longitude) {
      setError("Pickup location coordinates are not available.");
      setLoading(false);
      return;
    }

    const fetchProviders = async () => {
      try {
        const res = await getNearbyAmbulanceProviders({ latitude, longitude });
        setProviders(res.data?.data ?? []);
      } catch {
        setError("Failed to fetch ambulance providers. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchProviders();
  }, [latitude, longitude]);

  return (
    <div className={style.page}>
      <div className={style.header}>
        <div>
          <h2 className={style.title}>Nearby Ambulance Providers</h2>
          {!loading && !error && (
            <p className={style.subtitle}>
              {providers.length === 0
                ? "No ambulance providers found near the pickup location."
                : <>We can see <strong>{providers.length}</strong> ambulance provider{providers.length !== 1 ? "s" : ""} near the <span className={style.highlight}>pickup location</span></>
              }
            </p>
          )}
          {loading && <p className={style.subtitle}>Loading providers...</p>}
          {!loading && error && <p className={style.subtitle}>{error}</p>}
        </div>
        <button className={style.back_btn} onClick={() => navigate(-1)}>Back</button>
      </div>

      {!loading && !error && providers.length > 0 && (
        <div className={style.grid}>
          {providers.map((item) => (
            <AmbulanceProviderCard
              key={item._id}
              data={item}
              isShortlisted={shortlistedProviders.some((p) => p._id === item._id)}
              isSelected={reduxSelectedProviderId === item.provider._id}
              onShortlist={handleShortlist}
              shortlistedProviders={shortlistedProviders}
            />
          ))}
        </div>
      )}
    </div>
  );
};
